import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  Search as SearchIcon,
  Loader2,
  ArrowLeft,
  ArrowRight,
  X,
  AlertTriangle,
  ExternalLink,
} from "lucide-react";
import Logo from "@/components/Logo";
import { epcSearch, epcCertificate } from "@/lib/api";
import { getBrowserId } from "@/lib/browserId";

const ALLOWED_PARENTS = [
  "theepcregister.co.uk",
  "theepcman.co.uk",
  "chatepc.co.uk",
  "localhost",
  "emergentagent.com",
];

function parentAllowed() {
  const host = window.location.hostname.replace(/^www\./, "");
  if (window.top === window.self) {
    return ALLOWED_PARENTS.some((h) => host === h || host.endsWith("." + h));
  }

  // Collect every signal we have for the parent origin / hostname:
  //  1) window.location.ancestorOrigins (Safari + Chrome, reliable)
  //  2) ?parent=<hostname> query param set by embed.js
  //  3) document.referrer (unreliable on mobile with Referrer-Policy)
  const candidates = [];
  try {
    const ao = window.location.ancestorOrigins;
    if (ao && ao.length) {
      for (let i = 0; i < ao.length; i++) {
        try { candidates.push(new URL(ao[i]).hostname); } catch { /* ignore */ }
      }
    }
  } catch { /* ignore */ }
  try {
    const p = new URLSearchParams(window.location.search).get("parent");
    if (p) candidates.push(p);
  } catch { /* ignore */ }
  try {
    if (document.referrer) candidates.push(new URL(document.referrer).hostname);
  } catch { /* ignore */ }

  for (const raw of candidates) {
    const h2 = String(raw || "").replace(/^www\./, "").toLowerCase();
    if (ALLOWED_PARENTS.some((h) => h2 === h || h2.endsWith("." + h))) return true;
  }
  return false;
}

const BAND_COLOR = {
  A: "bg-epc-a text-white",
  B: "bg-epc-b text-black",
  C: "bg-epc-c text-black",
  D: "bg-epc-d text-black",
  E: "bg-epc-e text-black",
  F: "bg-epc-f text-white",
  G: "bg-epc-g text-white",
};

export default function EmbedCheck() {
  const [allowed] = useState(() => parentAllowed());
  const [postcode, setPostcode] = useState("");
  const [house, setHouse] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [cert, setCert] = useState(null);
  const [certLoading, setCertLoading] = useState(false);

  async function runSearch(e) {
    e && e.preventDefault();
    const q = postcode.trim();
    if (!q) return;
    setLoading(true);
    setResults(null);
    setSelected(null);
    setCert(null);
    try {
      const parentHost =
        (() => {
          try {
            const ao = window.location.ancestorOrigins;
            if (ao && ao.length) return new URL(ao[0]).hostname;
          } catch { /* ignore */ }
          try {
            const p = new URLSearchParams(window.location.search).get("parent");
            if (p) return p;
          } catch { /* ignore */ }
          try {
            if (document.referrer) return new URL(document.referrer).hostname;
          } catch { /* ignore */ }
          return null;
        })();
      const data = await epcSearch(q, {
        house_number: house,
        source: "embed-check",
        parent: parentHost,
        user_id: getBrowserId(),
      });
      setResults(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Lookup failed");
    } finally {
      setLoading(false);
    }
  }

  async function openCert(row) {
    setSelected(row);
    setCert(null);
    setCertLoading(true);
    try {
      const data = await epcCertificate(row.certificate_number, row.current_band);
      setCert({ ...data, current_band: row.current_band });
    } catch {
      toast.error("Could not load certificate detail");
    } finally {
      setCertLoading(false);
    }
  }

  if (!allowed) {
    return (
      <div className="min-h-screen grid place-items-center px-6 text-center font-sans bg-white">
        <div className="max-w-sm">
          <Logo className="h-10 w-10 mx-auto" />
          <h1 className="mt-4 font-heading text-lg tracking-tight">Not available on this site</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            ChatEPC is not authorised to be embedded on this domain.
          </p>
          <a href="https://chatepc.co.uk/check" className="inline-flex items-center gap-1 mt-4 text-sm text-epc-a">
            Open ChatEPC Check <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen w-screen flex flex-col bg-white font-sans"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
      data-testid="embed-check"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 h-14 border-b border-border shrink-0">
        <Logo className="h-6 w-6" />
        <div className="font-heading text-sm tracking-tight">
          Check EPC <span className="text-muted-foreground font-normal hidden sm:inline">· UK register</span>
        </div>
        <button
          type="button"
          onClick={() => window.parent && window.parent.postMessage({ chatepc: "close" }, "*")}
          data-testid="embed-check-close"
          className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 border border-border bg-white hover:bg-neutral-50 text-sm text-foreground"
          aria-label="Close EPC checker"
        >
          <X className="h-4 w-4" />
          <span>Close</span>
        </button>
      </div>

      {/* In-body always-visible close bar (for iOS Safari where the top
          header can be covered by the URL bar / notch). */}
      <button
        type="button"
        onClick={() => window.parent && window.parent.postMessage({ chatepc: "close" }, "*")}
        data-testid="embed-check-close-inline"
        className="w-full flex items-center justify-center gap-2 bg-neutral-100 border-b border-border py-2 text-sm text-foreground hover:bg-neutral-200"
        aria-label="Close EPC checker"
      >
        <X className="h-4 w-4" />
        <span className="font-medium">Close this box</span>
      </button>

      {/* Search bar */}
      <form onSubmit={runSearch} className="px-3 py-3 border-b border-border shrink-0">
        <div className="flex items-stretch border border-foreground/20 bg-white">
          <input
            data-testid="embed-check-house"
            value={house}
            onChange={(e) => setHouse(e.target.value)}
            placeholder="House no./name"
            className="w-28 min-w-0 outline-none px-2 py-2 text-sm bg-transparent border-r border-border"
            autoFocus
          />
          <div className="pl-2 flex items-center text-muted-foreground">
            <MapPin className="h-4 w-4" />
          </div>
          <input
            data-testid="embed-check-postcode"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            placeholder="Postcode e.g. LS1 4DL"
            className="flex-1 min-w-0 outline-none px-2 py-2 text-sm bg-transparent"
          />
          <Button
            type="submit"
            data-testid="embed-check-search"
            disabled={loading || !postcode.trim()}
            className="rounded-none bg-epc-a hover:bg-epc-a/90 text-white h-10 px-3"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <SearchIcon className="h-4 w-4" />}
          </Button>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          Live data from the UK government register (England &amp; Wales).
        </p>
      </form>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="py-10 grid place-items-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}

        {results && results.region === "scotland" && !loading && (
          <div className="p-4 text-center" data-testid="embed-check-region-scotland">
            <AlertTriangle className="h-6 w-6 text-epc-f mx-auto" />
            <h3 className="mt-3 font-heading text-base">{results.postcode} is in Scotland</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Scotland has its own EPC register separate from England &amp; Wales.
            </p>
            <a href="https://www.scottishepcregister.org.uk/" target="_blank" rel="noopener noreferrer" className="inline-block mt-3">
              <Button size="sm" className="rounded-none bg-epc-a hover:bg-epc-a/90 text-white">
                Open Scottish Register <ExternalLink className="ml-1 h-3.5 w-3.5" />
              </Button>
            </a>
          </div>
        )}

        {results && results.region === "ni" && !loading && (
          <div className="p-4 text-center" data-testid="embed-check-region-ni">
            <AlertTriangle className="h-6 w-6 text-epc-f mx-auto" />
            <h3 className="mt-3 font-heading text-base">{results.postcode} is in Northern Ireland</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Northern Ireland EPCs are held on a separate register.
            </p>
            <a href="https://www.epbni.com/" target="_blank" rel="noopener noreferrer" className="inline-block mt-3">
              <Button size="sm" className="rounded-none bg-epc-a hover:bg-epc-a/90 text-white">
                Open NI Register <ExternalLink className="ml-1 h-3.5 w-3.5" />
              </Button>
            </a>
          </div>
        )}

        {results && results.count === 0 && results.region === "ew" && !loading && (
          <div className="p-5 text-center" data-testid="embed-check-no-results">
            <AlertTriangle className="h-6 w-6 text-epc-f mx-auto" />
            <h3 className="mt-3 font-heading text-base">No certificates found</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              No EPCs on the England &amp; Wales register for <strong>{results.postcode}</strong>.
              Scotland and Northern Ireland have separate registers.
            </p>
          </div>
        )}

        {results && results.count > 0 && !selected && results.region === "ew" && (
          <>
          {results.house_number && results.house_matched === false && (
            <div className="m-3 flex items-start gap-2 border border-epc-c bg-epc-c/10 px-3 py-2 text-xs" data-testid="embed-check-house-no-match">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>No exact match for <strong>{results.house_number}</strong> — showing all certificates for {results.postcode}.</span>
            </div>
          )}
          <ul className="divide-y divide-border" data-testid="embed-check-results">
            {results.results.map((r) => (
              <li key={r.certificate_number}>
                <button
                  onClick={() => openCert(r)}
                  data-testid={`embed-check-row-${r.certificate_number}`}
                  className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-neutral-50"
                >
                  <div className={`h-10 w-10 grid place-items-center font-heading font-extrabold text-xl ${BAND_COLOR[(r.current_band||"?").toUpperCase()] || "bg-neutral-200 text-black"}`}>
                    {(r.current_band||"?").toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">
                      {[r.address_line_1, r.address_line_2].filter(Boolean).join(", ")}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {r.post_town} · {r.postcode} · {r.registration_date}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
          </>
        )}

        {selected && (
          <div className="p-3" data-testid="embed-check-detail">
            <button
              onClick={() => { setSelected(null); setCert(null); }}
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2"
            >
              <ArrowLeft className="h-3 w-3" /> Back to results
            </button>
            {certLoading && (
              <div className="py-6 grid place-items-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            )}
            {cert && <CertCard cert={cert} selected={selected} />}
          </div>
        )}
      </div>

      <div className="text-[10px] text-center text-muted-foreground py-1 border-t border-border shrink-0">
        Powered by ChatEPC · UK EPC assistant
      </div>
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="py-1.5">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-xs">{value}</div>
    </div>
  );
}

function CertCard({ cert, selected }) {
  const title = [selected.address_line_1, selected.address_line_2, selected.address_line_3].filter(Boolean).join(", ");
  return (
    <div className="border border-border">
      <div className="flex items-center gap-3 p-3 bg-neutral-50 border-b border-border">
        <div className={`h-10 w-10 grid place-items-center font-heading font-extrabold text-xl ${BAND_COLOR[(cert.current_band||"?").toUpperCase()] || "bg-neutral-200 text-black"}`}>
          {(cert.current_band||"?").toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-sm tracking-tight truncate">{title}</div>
          <div className="text-[11px] text-muted-foreground">{cert.post_town} · {cert.postcode}</div>
        </div>
      </div>
      <div className="px-3 py-2 divide-y divide-border">
        <Row label="Property type" value={[cert.property_type, cert.built_form].filter(Boolean).join(" · ")} />
        <Row label="Floor area" value={cert.total_floor_area_sqm ? `${cert.total_floor_area_sqm} m²` : null} />
        <Row label="Age band" value={cert.construction_age_band} />
        <Row label="Registered" value={cert.registration_date} />
        <Row label="Expires" value={cert.expiry_date} />
        <Row label="Main heating" value={cert.main_heating_description} />
        <Row label="Hot water" value={cert.hot_water_description} />
        <Row label="Walls" value={cert.walls_description} />
        <Row label="Roof" value={cert.roof_description} />
        <Row label="Windows" value={cert.windows_description} />
        <Row label="Lighting" value={cert.lighting_description} />
      </div>
    </div>
  );
}
